import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import MarkdownIt from 'markdown-it';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { Hash, Undo, Redo, Bold, Italic, List, ListOrdered, Wand, X,Image as ImageIcon } from 'lucide-react';
import { Note } from '../../services/notes';
import { useTranslation } from 'react-i18next';
import saveIcon from '../../assets/save_button.svg';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import PolishDialog from '../PolishDialog';
import { logEvent } from '../../utils/analytics';
import './styles.css';
import TurndownService from 'turndown';

interface TTEditorProps {
  selectedTag?: string | null;
  onSave: (content: string, updates?: Partial<Note>) => Promise<boolean>;
  initialNote?: Note;
  onCancel?: () => void;
  inline?: boolean;
  className?: string;
  noteId?: string;
}

interface SlashCommand {
  id: string;
  title: string;
  icon: React.ReactNode;
  action: () => void;
}

interface UploadedImage {
  _id: string;
  path: string;
  originalName: string;
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  user: string;
}

const TTEditor: React.FC<TTEditorProps> = ({
  selectedTag,
  onSave,
  initialNote,
  onCancel,
  inline = false,
  className = '',
  noteId
}) => {
  const { t } = useTranslation('noteeditor');
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState({ top: 0, left: 0 });
  const editorRef = useRef<HTMLDivElement>(null);
  const [polishing, setPolishing] = useState(false);
  const [showPolishDialog, setShowPolishDialog] = useState(false);
  const [polishResult, setPolishResult] = useState({ original: '', polished: '', images: [] as string[] });
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [wordCount, setWordCount] = useState({ characters: 0, words: 0 });
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  const md = new MarkdownIt();
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced'
  });

  const deleteImageFromServer = async (imageSrc: string): Promise<boolean> => {
    try {
      // 从完整的图片 URL 中提取文件名
      const filename = imageSrc.split('/').pop();
      if (!filename) {
        console.error('Invalid image path format:', imageSrc);
        return false;
      }

      console.log('Deleting image:', filename);

      // 获取图片列表
      const response = await fetch(`${API_BASE_URL}/images`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch images');
        return false;
      }

      const images = await response.json();
      // 根据文件名找到对应的图片
      const image = images.find((img: UploadedImage) => {
        console.log('img.filename:', img.filename);
        return img.filename === filename;
      });

      if (!image) {
        console.error('Image not found:', filename);
        return false;
      }

      console.log('Found image to delete:', image);

      // 使用图片 ID 删除
      const deleteResponse = await fetch(`${API_BASE_URL}/images/${image._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json();
        console.error('Failed to delete image:', errorData);
        return false;
      } else {
        console.log('Image deleted successfully:', image._id);
        // 重置文件输入框的值
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return true;
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      return false;
    }
  };

  const getImageIdFromSrc = async (src: string): Promise<string | null> => {
    try {
      const filename = src.split('/').pop();
      if (!filename) return null;

      const response = await fetch(`${API_BASE_URL}/images`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) return null;

      const images = await response.json();
      const image = images.find((img: UploadedImage) => img.filename === filename);
      return image ? image._id : null;
    } catch (error) {
      console.error('Error getting image ID:', error);
      return null;
    }
  };

  const CustomImage = Image.extend({
    addNodeView() {
      return ({ node, getPos, editor }) => {
        const container = document.createElement('div');
        container.style.position = 'relative';
        container.style.display = 'inline-block';
        
        // 添加一个数据属性用于跟踪
        container.dataset.imageContainer = 'true';

        const img = document.createElement('img');
        img.src = node.attrs.src;
        img.className = 'editor-image';
        
        const deleteButton = document.createElement('button');
        deleteButton.className = 'image-delete-button';
        deleteButton.innerHTML = '×';
        deleteButton.onclick = async (e) => {
          e.preventDefault();
          
          // 立即移除元素的事件和引用，防止 ResizeObserver 继续观察
          const imageToRemove = container.querySelector('img');
          if (imageToRemove) {
            // 清除可能存在的引用
            imageToRemove.onload = null;
            imageToRemove.onerror = null;
          }
          
          // 在删除前立即将元素从 DOM 中移除
          container.style.display = 'none';
          
          if (typeof getPos === 'function') {
            const position = getPos();
            
            if (initialNote?._id) {
              const imageId = await getImageIdFromSrc(node.attrs.src);
              if (imageId) {
                setImagesToDelete(prev => [...prev, imageId]);
              }
            } else {
              // 不要等待删除操作完成，先从 DOM 中移除
              deleteImageFromServer(node.attrs.src).catch(err => {
                console.error('Failed to delete image:', err);
              });
            }
            
            const view = editor?.view;
            if (view) {
              try {
                view.dispatch(
                  view.state.tr.delete(position, position + 1)
                );
              } catch (err) {
                console.error('Error dispatching delete:', err);
              }
            }
          }
        };

        container.appendChild(img);
        container.appendChild(deleteButton);

        return {
          dom: container,
          destroy: () => {
            // 清理工作，确保没有事件监听器遗留
            deleteButton.onclick = null;
            if (img) {
              img.onload = null;
              img.onerror = null;
            }
          }
        };
      };
    },

    addKeyboardShortcuts() {
      return {
        Backspace: ({ editor }) => {
          const { selection } = editor.state;
          const { empty } = selection;
          const $cursor = selection.$head;
          
          if (empty && $cursor) {
            const pos = $cursor.pos;
            const nodeBefore = $cursor.nodeBefore;
            
            if (nodeBefore && nodeBefore.type.name === 'image') {
              const imageSrc = nodeBefore.attrs.src;
              if (initialNote?._id) {
                getImageIdFromSrc(imageSrc).then(imageId => {
                  if (imageId) {
                    setImagesToDelete(prev => [...prev, imageId]);
                  }
                  editor.chain().focus().deleteRange({ from: pos - 1, to: pos }).run();
                });
              } else {
                deleteImageFromServer(imageSrc).then(success => {
                  if (success) {
                    editor.chain().focus().deleteRange({ from: pos - 1, to: pos }).run();
                  }
                });
              }
              return true;
            }
          }
          return false;
        },
        
        Delete: ({ editor }) => {
          const { selection } = editor.state;
          const node = editor.state.doc.nodeAt(selection.from);
          
          if (node?.type.name === 'image') {
            const imageSrc = node.attrs.src;
            deleteImageFromServer(imageSrc).then(success => {
              if (success) {
                return true;
              }
            });
            return true;
          }
          return false;
        }
      };
    }
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      CustomImage.configure({
        inline: true,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Placeholder.configure({
        placeholder: t('noteeditor.placeholder'),
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-node-empty',
      }),
      TaskList,
      TaskItem,
      Highlight,
      Link,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      if (error) {
        setError(null);
      }
    },
  });

  const slashCommands: SlashCommand[] = [
    {
      id: 'h1',
      title: '标题1',
      icon: 'H1',
      action: () => editor?.chain().focus().toggleHeading({ level: 1 }).run()
    },
    {
      id: 'h2',
      title: '标题2',
      icon: 'H2',
      action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run()
    },
    {
      id: 'bullet',
      title: '无序列表',
      icon: '•',
      action: () => editor?.chain().focus().toggleBulletList().run()
    },
    {
      id: 'number',
      title: '有序列表',
      icon: '1.',
      action: () => editor?.chain().focus().toggleOrderedList().run()
    },
    {
      id: 'quote',
      title: '引用',
      icon: '❝',
      action: () => editor?.chain().focus().toggleBlockquote().run()
    }
  ];

  useEffect(() => {
    if (selectedTag && editor) {
      const tagLine = `#${selectedTag}\n`;
      editor.commands.setContent(tagLine);
    } else {
      if (editor) {
        // 当 selectedTag 为 null 时（点击"所有笔记"），清空编辑器
        editor.commands.setContent('');
      }
    }
  }, [selectedTag, editor]);

  useEffect(() => {
    if (initialNote && editor) {
      const tagLine = initialNote.tags.map(tag => {
        let tagStr = `#${tag.level1}`;
        if (tag.level2) tagStr += `/${tag.level2}`;
        if (tag.level3) tagStr += `/${tag.level3}`;
        return tagStr;
      }).join(' ');

      let noteText = initialNote.text;
      // 移除可能存在的标签行
      noteText = removeExistingTags(noteText);
      
      if (noteText.includes('<')) {
        noteText = turndownService.turndown(noteText);
      } else {
        noteText = md.render(noteText);
      }

      const content = tagLine ? `${tagLine}\n${noteText}` : noteText;
      editor.commands.setContent(content);

      // 添加初始字数统计
      const text = editor.getText();
      const characters = text.length;
      const words = text.trim()
        .replace(/[\u4e00-\u9fa5]/g, '$& ')
        .split(/\s+/)
        .filter(word => word.length > 0).length;

      setWordCount({
        characters,
        words
      });
    }
  }, [initialNote, editor]);

  useEffect(() => {
    if (editor) {
      const element = editor.view.dom;

      const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        element.classList.add('drag-over');
      };

      const handleDragLeave = (e: DragEvent) => {
        e.preventDefault();
        element.classList.remove('drag-over');
      };

      const handleDrop = async (e: DragEvent) => {
        e.preventDefault();
        element.classList.remove('drag-over');
        
        if (e.dataTransfer?.files) {
          await handleImageUpload(e.dataTransfer.files);
        }
      };

      element.addEventListener('dragover', handleDragOver);
      element.addEventListener('dragleave', handleDragLeave);
      element.addEventListener('drop', handleDrop);

      return () => {
        element.removeEventListener('dragover', handleDragOver);
        element.removeEventListener('dragleave', handleDragLeave);
        element.removeEventListener('drop', handleDrop);
      };
    }
  }, [editor]);

  const handleSave = async () => {
    if (!editor) return;

    const content = editor.getText().trim();
    const { tagLine, content: contentWithoutTags } = extractTagsAndContent(content);

    if (!contentWithoutTags.trim()) {
      setError(t('noteeditor.empty_content'));
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const html = editor.getHTML();
      const markdown = turndownService.turndown(html);
      const cleanMarkdown = removeExistingTags(markdown);
      
      const finalContent = tagLine 
        ? `${tagLine}\n${cleanMarkdown}`
        : cleanMarkdown;

      const success = await onSave(finalContent);
      if (success) {
        // 保存成功后，处理要删除的图片
        if (imagesToDelete.length > 0) {
          try {
            await fetch(`${API_BASE_URL}/images/batch-delete`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                imageIds: imagesToDelete
              })
            });
          } catch (error) {
            console.error('Failed to delete images:', error);
          }
        }
        
        // 关联所有新上传的图片到笔记
        if (uploadedImages.length > 0 && initialNote?._id) {
          try {
            await fetch(`${API_BASE_URL}/images/associate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                noteId: initialNote._id,
                imageIds: uploadedImages
              })
            });
          } catch (error) {
            console.error('Failed to associate images:', error);
          }
        }
        
        // 清空上传图片记录
        setUploadedImages([]);
        setImagesToDelete([]); // 清空待删除图片列表
        const newContent = selectedTag ? `#${selectedTag}\n` : '';
        editor.commands.setContent(newContent);
      }
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    // 如果是新上传的图片，需要清理
    if (uploadedImages.length > 0) {
      try {
        console.log('Cleaning up images:', uploadedImages);
        // 修改请求路径，添加 /images 前缀
        const response = await fetch(`${API_BASE_URL}/images/cleanup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            imageIds: uploadedImages
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to cleanup images:', errorText);
        } else {
          const result = await response.json();
          console.log('Cleanup result:', result);
        }
      } catch (error) {
        console.error('Failed to cleanup images:', error);
      }
    }

    // 重置所有状态
    setUploadedImages([]);
    setImagesToDelete([]); // 清空待删除图片列表
    
    if (editor) {
      editor.commands.clearContent();
    }
    
    if (onCancel) {
      onCancel();
    }
  };

  const handlePolish = async () => {
    if (!editor) return;

    const html = editor.getHTML();
    const content = turndownService.turndown(html);
    const lines = content.split('\n');
    const tagLine = lines[0].startsWith('#') ? lines[0] : '';
    const contentToPolish = tagLine ? lines.slice(1).join('\n') : content;

    // 保存图片的 HTML 标签
    const images: string[] = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const imgElements = tempDiv.getElementsByTagName('img');
    Array.from(imgElements).forEach(img => {
      images.push(img.outerHTML);
    });

    // 移除图片后的内容
    const contentWithoutImages = contentToPolish.replace(/!\[.*?\]\(.*?\)/g, '');
    
    // 移除多余的空行
    const cleanContent = contentWithoutImages.split('\n')
      .filter(line => line.trim())
      .join('\n');

    if (cleanContent.trim()) {
      setPolishing(true);
      setPolishResult({
        original: cleanContent,
        polished: '',
        images
      });
      setShowPolishDialog(true);

      logEvent('Feature', 'Polish', 'Start');

      try {
        const response = await fetch(`${API_BASE_URL}/polish`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content: cleanContent })  // 发送不包含图片的内容
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === 'content') {
                    accumulatedContent += data.content;
                    setPolishResult(prev => ({
                      ...prev,
                      polished: accumulatedContent
                    }));
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e);
                }
              }
            }
          }
        }
        logEvent('Feature', 'Polish', 'Complete');
      } catch (error) {
        console.error('Error during polishing:', error);
        logEvent('Feature', 'Polish', 'Error');
      } finally {
        setPolishing(false);
      }
    }
  };

  const handleAcceptPolish = () => {
    if (!editor) return;

    const tagLine = editor.getText().split('\n')[0];
    
    if (tagLine.startsWith('#')) {
      const polishedHtml = md.render(polishResult.polished, {
        html: true
      });
      
      // 在润色后的内容后面添加图片 HTML
      const contentWithImages = polishedHtml + 
        (polishResult.images.length > 0 ? '<br>' + polishResult.images.join('') : '');
      
      const contentWithBreaks = `${tagLine}<br>${contentWithImages}`;
      editor.commands.setContent(contentWithBreaks);
    } else {
      const polishedHtml = md.render(polishResult.polished, {
        html: true
      });
      
      // 在润色后的内容后面添加图片 HTML
      const contentWithImages = polishedHtml + 
        (polishResult.images.length > 0 ? '<br><br>' + polishResult.images.join('') : '');
      
      editor.commands.setContent(contentWithImages);
    }

    setShowPolishDialog(false);
    logEvent('Feature', 'Polish', 'Accept');
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || !files.length) return;
    
    if (files.length > 5) {
      setError('最多只能同时上传5张图片');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('images', file);
    });
    
    try {
      const response = await fetch(`${API_BASE_URL}/images/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const images: UploadedImage[] = await response.json();
      
      // 保存新上传图片的ID
      const newImageIds = images.map(img => img._id);
      console.log('Adding new image IDs:', newImageIds);
      setUploadedImages(prev => [...prev, ...newImageIds]);
      
      images.forEach((image: UploadedImage) => {
        editor?.chain().focus().setImage({ 
          src: `${API_BASE_URL}/uploads${image.path}`,
          alt: image.originalName || 'Uploaded image',
          title: image.originalName
        }).run();
      });
    } catch (error) {
      console.error('Image upload failed:', error);
      setError('图片上传失败，请重试');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const extractTagsAndContent = (text: string) => {
    const lines = text.split('\n');
    const firstLine = lines[0];
    
    if (firstLine.startsWith('#')) {
      return {
        tagLine: firstLine,
        content: lines.slice(1).join('\n')
      };
    }
    
    return {
      tagLine: '',
      content: text
    };
  };

  const removeExistingTags = (content: string) => {
    const lines = content.split('\n');
    const contentLines = lines.filter(line => !line.trim().startsWith('#'));
    return contentLines.join('\n');
  };

  useLayoutEffect(() => {
    // 防止 ResizeObserver 警告
    const resizeObserverError = (error: ErrorEvent) => {
      // 确保 error.message 存在且为字符串
      if (error && error.message) {
        if (error.message === 'ResizeObserver loop completed with undelivered notifications.' || 
            (typeof error.message === 'string' && error.message.includes('ResizeObserver'))) {
          error.stopImmediatePropagation();
          // 阻止默认行为以防止错误被打印到控制台
          if (error.preventDefault) {
            error.preventDefault();
          }
          return false;
        }
      }
    };

    // 添加更多的错误捕获方式
    window.addEventListener('error', resizeObserverError, true);
    
    // 添加对未处理的 Promise 拒绝的处理
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event && event.reason && 
          typeof event.reason === 'object' && 
          event.reason !== null && 
          typeof event.reason.message === 'string' && 
          event.reason.message.includes('ResizeObserver')) {
        event.preventDefault();
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', resizeObserverError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // 添加处理标签插入的函数
  const handleInsertTag = () => {
    if (editor) {
      editor.chain().focus().insertContent('#').run();
    }
  };

  useEffect(() => {
    if (editor) {
      const updateWordCount = () => {
        const text = editor.getText();
        // 对于中文和英文混合的文本，我们需要特别处理
        const characters = text.length;
        // 使用正则表达式分割中英文单词
        const words = text.trim()
          .replace(/[\u4e00-\u9fa5]/g, '$& ') // 在中文字符后添加空格
          .split(/\s+/)
          .filter(word => word.length > 0).length;

        setWordCount({
          characters,
          words
        });
      };

      editor.on('update', updateWordCount);
      return () => {
        editor.off('update', updateWordCount);
      };
    }
  }, [editor]);

  // 添加样式
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .editor-image {
        max-width: 100%;
        vertical-align: top;
      }
      
      .ProseMirror div {
        position: relative;
        display: inline-block;
        /* 移除任何可能导致悬停效果的样式 */
      }
      
      .image-delete-button {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.6);
        border: none;
        border-radius: 50%;
        color: white;
        font-size: 18px;
        display: none;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: opacity 0.2s;
        z-index: 999;
        pointer-events: all;
      }

      .image-delete-button:hover {
        background: rgba(0, 0, 0, 0.8);
      }

      /* 只对删除按钮应用悬停效果，而不是整个图片 */
      .ProseMirror div:hover .image-delete-button {
        display: flex;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!editor) {
    return null;
  }

  return (
    <>
      <div className={`note-editor ${inline ? 'inline' : ''} ${className}`}>
        {error && <div className="error-message">{error}</div>}
        <div className="editor-wrapper" ref={editorRef}>
          <EditorContent editor={editor} className="editor-content" />
          <div className="word-count">
            {t('noteeditor.word_count', { words: wordCount.words, characters: wordCount.characters })}
          </div>
          <div className="editor-toolbar">
            <div className="toolbar-buttons">
              <button
                onClick={handleInsertTag}
                className="editor-button"
                title={t('noteeditor.add_tag')}
              >
                <Hash size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="editor-button"
                title={t('noteeditor.undo')}
              >
                <Undo size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="editor-button"
                title={t('noteeditor.redo')}
              >
                <Redo size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`editor-button ${editor.isActive('bold') ? 'is-active' : ''}`}
                title={t('noteeditor.bold')}
              >
                <Bold size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`editor-button ${editor.isActive('italic') ? 'is-active' : ''}`}
                title={t('noteeditor.italic')}
              >
                <Italic size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`editor-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
                title={t('noteeditor.unordered_list')}
              >
                <List size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`editor-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
                title={t('noteeditor.ordered_list')}
              >
                <ListOrdered size={16} />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`editor-button ${uploading ? 'uploading' : ''}`}
                title={t('noteeditor.upload_image')}
                disabled={uploading}
              >
                <ImageIcon size={16} />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleImageUpload(e.target.files)}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                maxLength={5}
              />
              <button
                onClick={handlePolish}
                title={t('noteeditor.polish')}
                className={`editor-button ${polishing ? 'polishing' : ''}`}
                disabled={polishing}
              >
                <Wand size={16} />
              </button>
            </div>
            <div className="editor-actions">
              {onCancel && (
                <button
                  className="cancel-btn"
                  onClick={handleCancel}
                  title={t('noteeditor.cancel')}
                >
                  <X size={16} />
                </button>
              )}
              <button
                className={`save-btn ${saving ? 'saving' : ''}`}
                onClick={handleSave}
                disabled={saving}
                title={t('noteeditor.save')}
              >
                <img
                  src={saveIcon}
                  alt="Save"
                  className="save-icon"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      {showPolishDialog && (
        <PolishDialog
          originalContent={polishResult.original}
          polishedContent={polishResult.polished}
          isPolishing={polishing}
          onAccept={handleAcceptPolish}
          onCancel={() => setShowPolishDialog(false)}
        />
      )}
    </>
  );
};

export default TTEditor;